import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styles from './Buy.module.css';
import PricingMenu, { type Plan } from '../components/PricingMenu';
import {
  trackAffiliatePurchase,
  checkCustomerEmail,
  validateCode,
  redeemCoupon,
} from '../utils/api';
import type { CodeValidationResult } from '../utils/api';
import { trackPurchase, trackButtonClick, trackEvent } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';

const WIZARD_STEPS = [
  { key: 'product', label: 'Product' },
  { key: 'identify', label: 'Your Info' },
  { key: 'code', label: 'Code' },
  { key: 'payment', label: 'Payment' },
] as const;

type WizardStep = (typeof WIZARD_STEPS)[number]['key'];

// Function to create customer record
const createCustomer = async (name: string, email: string) => {
  try {
    const response = await fetch('/api/customers', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: name || null,
        email: email || null,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create customer');
    }

    const data = await response.json();
    return data.customer;
  } catch (error) {
    console.error('Error creating customer:', error);
    return null;
  }
};

export default function Buy() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, authEnabled } = useAuth();
  const paypalRef = useRef<HTMLDivElement | null>(null);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('product');

  // Step 1: Product selection
  const [selectedProduct, setSelectedProduct] = useState<'desktop' | 'mpdId'>('desktop');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedPlanMeta, setSelectedPlanMeta] = useState<Plan | null>(null);
  const [selectedPlanType, setSelectedPlanType] = useState('one-time');
  const [currentPrice, setCurrentPrice] = useState('49.99');
  const [webIdPurchaseEnabled, setWebIdPurchaseEnabled] = useState<boolean | null>(null);

  // Step 2: User identification
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [emailChecking, setEmailChecking] = useState(false);
  const [existingCustomer, setExistingCustomer] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [emailChecked, setEmailChecked] = useState(false);

  // Step 3: Code entry (unified affiliate + coupon)
  const [enteredCode, setEnteredCode] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeResult, setCodeResult] = useState<CodeValidationResult | null>(null);
  const [redeemError, setRedeemError] = useState('');
  const [redeemingCoupon, setRedeemingCoupon] = useState(false);

  // Step 4: Payment
  const [sdkReady, setSdkReady] = useState(false);
  const [paypalError, setPaypalError] = useState('');

  // Derived: does the user need to pay?
  const paymentRequired = !codeResult?.valid || codeResult.paymentRequired !== false;

  // Derived: effective price after discount
  const effectivePrice = useCallback((): string => {
    if (!codeResult?.valid || !selectedPlanMeta) return currentPrice;
    if (codeResult.type === 'affiliate') return currentPrice; // affiliates don't discount price
    if (codeResult.discountType === 'free') return '0.00';
    if (codeResult.discountType === 'percent' && codeResult.discountValue) {
      const discounted = parseFloat(currentPrice) * (1 - codeResult.discountValue / 100);
      return Math.max(0, discounted).toFixed(2);
    }
    if (codeResult.discountType === 'fixed' && codeResult.discountValue) {
      const discounted = parseFloat(currentPrice) - codeResult.discountValue;
      return Math.max(0, discounted).toFixed(2);
    }
    return currentPrice;
  }, [codeResult, currentPrice, selectedPlanMeta]);

  // --- Feature flag fetch ---
  useEffect(() => {
    const fetchPurchaseEnabled = async () => {
      try {
        const response = await fetch('/api/feature-flags/web_id_purchase_enabled');
        if (!response.ok) {
          setWebIdPurchaseEnabled(null);
          return;
        }
        const data = await response.json();
        const value = (data && data.value) || null;
        const enabled =
          value && typeof value === 'object' && 'enabled' in value
            ? Boolean((value as any).enabled)
            : null;
        setWebIdPurchaseEnabled(enabled);
      } catch (e) {
        console.error('Error loading feature flags:', e);
        setWebIdPurchaseEnabled(null);
      }
    };

    fetchPurchaseEnabled();
  }, []);

  // --- URL query params for ref code and product ---
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    const product = params.get('product');

    if (product === 'mpdId') setSelectedProduct('mpdId');
    if (product === 'desktop') setSelectedProduct('desktop');

    if (refCode) {
      setEnteredCode(refCode);
      // We'll validate when they reach step 3
    }
  }, [location]);

  // --- Reset plan when product changes ---
  useEffect(() => {
    setSelectedPlan(null);
    setSelectedPlanMeta(null);
    setSdkReady(false);
    setPaypalError('');
  }, [selectedProduct]);

  // --- Plan selection handler ---
  const handlePlanSelect = (plan: Plan) => {
    if (selectedProduct === 'mpdId') {
      if (!authEnabled) {
        setPaypalError('Login is required but authentication is not configured.');
        return;
      }
      if (!currentUser) {
        navigate('/login', { state: { from: { pathname: '/buy' } } });
        return;
      }
    }

    trackButtonClick(`select_plan_${plan.id}`, 'buy_page');
    trackEvent('select_item', 'ecommerce', plan.id, parseFloat(plan.price));

    setSelectedPlan(plan.id);
    setSelectedPlanMeta(plan);
    setSelectedPlanType(plan.type);
    setCurrentPrice(plan.price);
    setPaypalError('');
  };

  // --- Email check handler ---
  const handleCheckEmail = useCallback(async () => {
    if (!customerEmail) return;
    setEmailChecking(true);
    const result = await checkCustomerEmail(customerEmail);
    setEmailChecked(true);
    if (result.exists && result.customer) {
      setExistingCustomer(result.customer);
      if (result.customer.name && !customerName) {
        setCustomerName(result.customer.name);
      }
    } else {
      setExistingCustomer(null);
    }
    setEmailChecking(false);
  }, [customerEmail, customerName]);

  // --- Code validation with debounce ---
  useEffect(() => {
    if (!enteredCode) {
      setCodeResult(null);
      return;
    }

    setCodeChecking(true);
    const timer = setTimeout(() => {
      validateCode(enteredCode, selectedProduct)
        .then(result => {
          setCodeResult(result);
          setCodeChecking(false);
        })
        .catch(() => {
          setCodeResult({ valid: false, error: 'Failed to validate code' });
          setCodeChecking(false);
        });
    }, 500);

    return () => clearTimeout(timer);
  }, [enteredCode, selectedProduct]);

  // --- Load PayPal SDK ---
  useEffect(() => {
    const existingScript = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (existingScript) existingScript.remove();

    setSdkReady(false);
    setPaypalError('');

    if (currentStep !== 'payment' || !paymentRequired) return;

    const script = document.createElement('script');
    const clientId =
      import.meta.env.VITE_PAYPAL_CLIENT_ID ||
      'not_AZDxjDScFpQtjWTOUtWKbyN_bDt4OgqaF4eYXlewfBP4-8aqX3PiV8e1GWU6liB2CUXlkA59kJXE7M6R';

    const intent = selectedPlanType === 'subscription' ? 'subscription' : 'capture';
    const vaultParam = selectedPlanType === 'subscription' ? '&vault=true' : '';
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}${vaultParam}&intent=${intent}`;
    script.async = true;

    script.onload = () => {
      setSdkReady(true);
    };
    script.onerror = () => {
      setPaypalError('Failed to load PayPal. Please check your internet connection.');
    };

    document.body.appendChild(script);

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [selectedPlanType, currentStep, paymentRequired]);

  // --- Render PayPal buttons ---
  useEffect(() => {
    if (currentStep !== 'payment' || !paymentRequired || !sdkReady || !paypalRef.current) return;

    paypalRef.current.innerHTML = '';

    const paypal = window.paypal as any;
    if (!paypal) {
      setPaypalError('PayPal SDK failed to load properly. Please refresh the page.');
      return;
    }

    try {
      const planDetails = {
        id: selectedPlanMeta?.id ?? selectedPlan,
        name: selectedPlanMeta?.name ?? 'Unknown',
        features: selectedPlanMeta?.features ?? [],
      };

      const finalPrice = effectivePrice();
      const affiliateCode = codeResult?.type === 'affiliate' ? enteredCode : '';
      const referredByCode = codeResult?.valid ? enteredCode : '';
      const referredByType = codeResult?.valid ? codeResult.type : '';

      const buttonConfig: any = {
        style: { color: 'gold', shape: 'rect', layout: 'vertical' },
        onError: (err: any) => {
          console.error('PayPal error:', err);
          setPaypalError('Error occurred during payment process.');
        },
        onCancel: () => {
          setPaypalError('');
          trackEvent('payment_cancelled', 'ecommerce', selectedPlan ?? '');
        },
      };

      if (selectedPlanType === 'subscription') {
        buttonConfig.createSubscription = (_data: any, actions: any) => {
          const environment = import.meta.env.VITE_ENVIRONMENT;
          const desktopPlanId =
            environment === 'production' ? 'P-0WH86474MT672220NNCINGGY' : 'P-SANDBOX-PLAN-ID-HERE';
          const mpdIdPlanId =
            environment === 'production'
              ? import.meta.env.VITE_PAYPAL_MPD_ID_PLAN_ID_LIVE
              : import.meta.env.VITE_PAYPAL_MPD_ID_PLAN_ID_SANDBOX;

          const planId = selectedProduct === 'mpdId' ? mpdIdPlanId : desktopPlanId;

          if (!planId) {
            setPaypalError('Subscription is not configured. Please try again later.');
            return Promise.reject(new Error('Subscription plan is not configured'));
          }

          return actions.subscription.create({
            plan_id: planId,
            custom_id:
              selectedProduct === 'mpdId' ? (currentUser?.uid ?? planDetails.id) : planDetails.id,
            application_context: {
              brand_name: 'MyPeopleDatabase',
              locale: 'en-US',
              shipping_preference: 'NO_SHIPPING',
              user_action: 'SUBSCRIBE_NOW',
              payment_method: {
                payer_selected: 'PAYPAL',
                payee_preferred: 'IMMEDIATE_PAYMENT_REQUIRED',
              },
              return_url: `${window.location.origin}${selectedProduct === 'mpdId' ? '/user-profile-dashboard' : '/download'}`,
              cancel_url: `${window.location.origin}/buy`,
            },
          });
        };

        buttonConfig.onApprove = async (data: any) => {
          try {
            if (customerName || customerEmail) {
              await createCustomer(customerName, customerEmail);
            }

            trackPurchase(data.subscriptionID, parseFloat(finalPrice), 'USD', [
              {
                item_id: selectedPlan,
                item_name: planDetails.name,
                category: 'subscription',
                quantity: 1,
                price: parseFloat(finalPrice),
              },
            ]);

            if (referredByCode) {
              trackAffiliatePurchase(
                data.subscriptionID,
                finalPrice,
                affiliateCode,
                'subscription',
                referredByCode,
                referredByType
              ).catch(error => console.error('Error tracking affiliate purchase:', error));
            }

            navigate(selectedProduct === 'mpdId' ? '/user-profile-dashboard' : '/download');
          } catch (error) {
            console.error('Error processing subscription approval:', error);
            setPaypalError('Error processing your subscription. Please try again.');
          }
        };
      } else {
        buttonConfig.createOrder = (_data: any, actions: any) => {
          return actions.order.create({
            purchase_units: [
              {
                custom_id: planDetails.id,
                description: `MyPeopleDatabase ${planDetails.name} Plan`,
                items: [
                  {
                    name: `${planDetails.name} Plan`,
                    description: planDetails.features.join(', '),
                    unit_amount: { currency_code: 'USD', value: finalPrice },
                    quantity: '1',
                  },
                ],
                amount: {
                  value: finalPrice,
                  currency_code: 'USD',
                  breakdown: {
                    item_total: { currency_code: 'USD', value: finalPrice },
                  },
                },
                custom_data: affiliateCode ? { affiliate_code: affiliateCode } : undefined,
              },
            ],
          });
        };

        buttonConfig.onApprove = (_data: any, actions: any) => {
          return actions.order
            .capture()
            .then(async function (details: any) {
              try {
                if (customerName || customerEmail) {
                  await createCustomer(customerName, customerEmail);
                }

                trackPurchase(details.id, parseFloat(finalPrice), 'USD', [
                  {
                    item_id: selectedPlan,
                    item_name: planDetails.name,
                    category: 'one-time',
                    quantity: 1,
                    price: parseFloat(finalPrice),
                  },
                ]);

                if (referredByCode) {
                  trackAffiliatePurchase(
                    details.id,
                    finalPrice,
                    affiliateCode,
                    'one-time',
                    referredByCode,
                    referredByType
                  ).catch(error => console.error('Error tracking affiliate purchase:', error));
                }

                navigate(selectedProduct === 'mpdId' ? '/user-profile-dashboard' : '/download');
              } catch (error) {
                console.error('Error processing payment completion:', error);
                setPaypalError('Error processing your payment. Please contact support.');
              }
            })
            .catch((error: any) => {
              console.error('PayPal order capture failed:', error);
              setPaypalError('Payment capture failed. Please try again.');
            });
        };
      }

      paypal.Buttons(buttonConfig).render(paypalRef.current);
    } catch (error) {
      console.error('Failed to render PayPal buttons:', error);
      setPaypalError('Failed to display payment options.');
    }
  }, [
    sdkReady,
    currentStep,
    paymentRequired,
    currentPrice,
    selectedPlanType,
    selectedPlan,
    selectedPlanMeta,
    enteredCode,
    codeResult,
    customerName,
    customerEmail,
    selectedProduct,
    navigate,
    effectivePrice,
    currentUser,
  ]);

  // --- Free coupon redemption ---
  const handleRedeemFreeCoupon = async () => {
    if (!codeResult?.valid || codeResult.type !== 'coupon') return;
    setRedeemingCoupon(true);
    setRedeemError('');

    const result = await redeemCoupon(
      enteredCode,
      customerEmail,
      selectedProduct,
      enteredCode,
      codeResult.type
    );
    if (result.success) {
      trackEvent('coupon_redeemed', 'ecommerce', enteredCode);
      if (customerName || customerEmail) {
        await createCustomer(customerName, customerEmail);
      }
      navigate(selectedProduct === 'mpdId' ? '/user-profile-dashboard' : '/download');
    } else {
      setRedeemError(result.error || 'Failed to redeem coupon');
    }
    setRedeemingCoupon(false);
  };

  // --- Plans data ---
  const mpdIdEnabled = webIdPurchaseEnabled !== false;

  const desktopPlans: Plan[] = [
    {
      id: 'basic',
      name: 'Desktop App - Basic',
      price: '49.99',
      type: 'one-time',
      features: ['Lifetime Access', 'Basic Support', 'Core Features'],
      disabled: false,
    },
    {
      id: 'subscription',
      name: 'Desktop App - Annual Subscription',
      price: '39.99',
      type: 'subscription',
      features: [
        'Yearly Recurring Access',
        'Priority Support',
        'All Features',
        'Automatic Updates',
      ],
      disabled: false,
      popular: true,
    },
  ];

  const mpdIdPlans: Plan[] = [
    {
      id: 'mpd-id-subscription',
      name: 'MPD ID Page - Annual Subscription',
      price: '10.00',
      type: 'subscription',
      features: [
        'Public MPD ID page',
        'Shareable profile link',
        'Contact details visible when active',
      ],
      disabled: !mpdIdEnabled || !authEnabled || !currentUser,
      popular: true,
    },
  ];

  const plansForSelectedProduct = selectedProduct === 'mpdId' ? mpdIdPlans : desktopPlans;

  // --- Step navigation helpers ---
  const stepIndex = WIZARD_STEPS.findIndex(s => s.key === currentStep);

  const canAdvance = (): boolean => {
    switch (currentStep) {
      case 'product':
        return !!selectedPlan;
      case 'identify':
        return true; // email is optional but encouraged
      case 'code':
        return true; // code is optional
      case 'payment':
        return false;
      default:
        return false;
    }
  };

  const goNext = () => {
    if (currentStep === 'identify' && customerEmail && !emailChecked) {
      handleCheckEmail().then(() => {
        const nextIdx = stepIndex + 1;
        if (nextIdx < WIZARD_STEPS.length) setCurrentStep(WIZARD_STEPS[nextIdx].key);
      });
      return;
    }

    // If on the code step and the coupon makes it free, go to payment step which will show the claim button
    const nextIdx = stepIndex + 1;
    if (nextIdx < WIZARD_STEPS.length) {
      setCurrentStep(WIZARD_STEPS[nextIdx].key);
    }
  };

  const goBack = () => {
    const prevIdx = stepIndex - 1;
    if (prevIdx >= 0) setCurrentStep(WIZARD_STEPS[prevIdx].key);
  };

  const goToStep = (step: WizardStep) => {
    const targetIdx = WIZARD_STEPS.findIndex(s => s.key === step);
    // Allow going back to any earlier step, or forward only if current step is valid
    if (targetIdx <= stepIndex) {
      setCurrentStep(step);
    }
  };

  // --- Render ---
  return (
    <>
      <Helmet>
        <title>Pricing & Plans | MyPeopleDatabase Contact Manager</title>
        <meta
          name="description"
          content="View MyPeopleDatabase pricing and plans, including one-time purchase and annual subscription options. Compare features, choose your plan, and securely pay with PayPal."
        />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.downloadCTA}>
            <h2>Ready to try MyPeopleDatabase?</h2>
            <p>
              Get started right away with our free download, or purchase a license for premium
              features.
            </p>
            <button onClick={() => navigate('/download')} className={styles.freeDownloadButton}>
              Download Now
            </button>
          </div>

          <div className={styles.systemRequirements}>
            <h2>Supported Platforms & Requirements</h2>
            <div className={styles.requirementsGrid}>
              <div className={styles.requirementsColumn}>
                <h3>Operating Systems</h3>
                <ul>
                  <li>Windows 10/11</li>
                  <li>macOS 12.0+</li>
                  {/* <li>Linux (Ubuntu 20.04+, Fedora 36+)</li> */}
                </ul>
              </div>
              <div className={styles.requirementsColumn}>
                <h3>Minimum Requirements</h3>
                <ul>
                  <li>2 GB RAM</li>
                  <li>500 MB disk space</li>
                  <li>1024×768 screen resolution</li>
                  <li>Internet connection for syncing - coming soon</li>
                </ul>
              </div>
              <div className={styles.requirementsColumn}>
                <h3>Compatibility</h3>
                <ul>
                  <li>MS Office integration</li>
                  <li>Google Contacts sync</li>
                  <li>CSV import/export</li>
                  <li>Local data storage</li>
                </ul>
              </div>
            </div>
          </div>

          <h1 className={styles.title}>Choose Your Plan</h1>
          <p className={styles.subtitle}>
            Limited-time 50% off sale pricing is already applied to the prices below.
          </p>

          {/* Wizard Step Indicators */}
          <div className={styles.wizardSteps}>
            {WIZARD_STEPS.map((step, idx) => (
              <div
                key={step.key}
                className={`${styles.wizardStep} ${currentStep === step.key ? styles.wizardStepActive : ''} ${idx < stepIndex ? styles.wizardStepCompleted : ''}`}
                onClick={() => goToStep(step.key)}
              >
                <div className={styles.wizardStepNumber}>
                  {idx < stepIndex ? (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </div>
                <span className={styles.wizardStepLabel}>{step.label}</span>
                {idx < WIZARD_STEPS.length - 1 && <div className={styles.wizardStepConnector} />}
              </div>
            ))}
          </div>

          {/* ========== STEP 1: PRODUCT SELECTION ========== */}
          {currentStep === 'product' && (
            <div className={styles.wizardPanel}>
              <h2 className={styles.checkoutTitle}>Select a Product & Plan</h2>

              <div className={styles.productSelector}>
                <button
                  type="button"
                  className={`${styles.productButton} ${selectedProduct === 'desktop' ? styles.productButtonSelected : ''}`}
                  onClick={() => setSelectedProduct('desktop')}
                >
                  Desktop App
                </button>
                <button
                  type="button"
                  className={`${styles.productButton} ${selectedProduct === 'mpdId' ? styles.productButtonSelected : ''} ${!mpdIdEnabled ? styles.productButtonDisabled : ''}`}
                  onClick={() => {
                    if (mpdIdEnabled) setSelectedProduct('mpdId');
                  }}
                >
                  MPD ID Page
                </button>
              </div>

              {!mpdIdEnabled && selectedProduct === 'mpdId' && (
                <div className={styles.purchaseDisabledNotice}>
                  MPD ID Page purchases are temporarily disabled.
                </div>
              )}

              {mpdIdEnabled && selectedProduct === 'mpdId' && (!authEnabled || !currentUser) && (
                <div className={styles.purchaseDisabledNotice}>
                  Please{' '}
                  <Link to="/login" state={{ from: { pathname: '/buy?product=mpdId' } }}>
                    log in
                  </Link>{' '}
                  or{' '}
                  <Link to="/signup" state={{ from: { pathname: '/buy?product=mpdId' } }}>
                    sign up
                  </Link>{' '}
                  to purchase an MPD ID Page subscription.
                </div>
              )}

              <PricingMenu
                selectedPlan={selectedPlan}
                onSelectPlan={handlePlanSelect}
                plans={plansForSelectedProduct}
              />

              {selectedPlan && (
                <div className={styles.selectedPlanInfo}>
                  Selected: {selectedPlanMeta?.name || selectedPlan} - ${currentPrice}
                  {selectedPlanType === 'subscription' ? '/year' : ''}
                </div>
              )}

              <div className={styles.wizardNav}>
                <div />
                <button
                  type="button"
                  className={styles.wizardNextButton}
                  disabled={!canAdvance()}
                  onClick={goNext}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ========== STEP 2: USER IDENTIFICATION ========== */}
          {currentStep === 'identify' && (
            <div className={styles.wizardPanel}>
              <h2 className={styles.checkoutTitle}>Your Information</h2>
              <p className={styles.wizardDescription}>
                Enter your email so we can look up your account or create a new one.
              </p>

              <div className={styles.customerInfoSection}>
                <div className={styles.customerFields}>
                  <div className={styles.fieldGroup}>
                    <label htmlFor="customerEmail" className={styles.fieldLabel}>
                      Email Address
                    </label>
                    <div className={styles.affiliateInputWrapper}>
                      <input
                        type="email"
                        id="customerEmail"
                        className={`${styles.fieldInput} ${existingCustomer ? styles.validInput : ''}`}
                        value={customerEmail}
                        onChange={e => {
                          setCustomerEmail(e.target.value);
                          setEmailChecked(false);
                          setExistingCustomer(null);
                        }}
                        onBlur={() => {
                          if (customerEmail) handleCheckEmail();
                        }}
                        placeholder="Enter your email"
                      />
                      {emailChecking && (
                        <span className={styles.affiliateCodeStatus}>Checking...</span>
                      )}
                      {!emailChecking && emailChecked && existingCustomer && (
                        <span className={styles.affiliateCodeValid}>Welcome back!</span>
                      )}
                      {!emailChecking && emailChecked && !existingCustomer && customerEmail && (
                        <span className={styles.affiliateCodeValid}>New customer</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label htmlFor="customerName" className={styles.fieldLabel}>
                      Name (Optional)
                    </label>
                    <input
                      type="text"
                      id="customerName"
                      className={styles.fieldInput}
                      value={customerName}
                      onChange={e => setCustomerName(e.target.value)}
                      placeholder="Enter your name"
                    />
                  </div>
                </div>
              </div>

              <div className={styles.wizardNav}>
                <button type="button" className={styles.wizardBackButton} onClick={goBack}>
                  Back
                </button>
                <button type="button" className={styles.wizardNextButton} onClick={goNext}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* ========== STEP 3: CODE ENTRY ========== */}
          {currentStep === 'code' && (
            <div className={styles.wizardPanel}>
              <h2 className={styles.checkoutTitle}>Have a Code?</h2>
              <p className={styles.wizardDescription}>
                Enter an affiliate or coupon code if you have one. This is optional.
              </p>

              <div className={styles.affiliateSection}>
                <label htmlFor="enteredCode" className={styles.affiliateLabel}>
                  Affiliate or Coupon Code
                </label>
                <div className={styles.affiliateInputWrapper}>
                  <input
                    type="text"
                    id="enteredCode"
                    className={`${styles.affiliateInput} ${codeResult && !codeChecking ? (codeResult.valid ? styles.validInput : styles.invalidInput) : ''}`}
                    value={enteredCode}
                    onChange={e => {
                      setEnteredCode(e.target.value.trim());
                      setCodeResult(null);
                      setRedeemError('');
                    }}
                    placeholder="Enter your code"
                  />
                  {codeChecking && <span className={styles.affiliateCodeStatus}>Checking...</span>}
                  {!codeChecking && codeResult?.valid && codeResult.type === 'affiliate' && (
                    <span className={styles.affiliateCodeValid}>Affiliate code</span>
                  )}
                  {!codeChecking &&
                    codeResult?.valid &&
                    codeResult.type === 'coupon' &&
                    !codeResult.paymentRequired && (
                      <span className={styles.affiliateCodeValid}>Free access!</span>
                    )}
                  {!codeChecking &&
                    codeResult?.valid &&
                    codeResult.type === 'coupon' &&
                    codeResult.paymentRequired && (
                      <span className={styles.affiliateCodeValid}>
                        Coupon:{' '}
                        {codeResult.discountType === 'percent'
                          ? `${codeResult.discountValue}% off`
                          : `$${codeResult.discountValue} off`}
                      </span>
                    )}
                  {!codeChecking && codeResult && !codeResult.valid && enteredCode && (
                    <span className={styles.affiliateCodeInvalid}>
                      {codeResult.error || 'Invalid code'}
                    </span>
                  )}
                </div>

                {codeResult?.valid && codeResult.partnerName && (
                  <div className={styles.codePartnerInfo}>Partner: {codeResult.partnerName}</div>
                )}
              </div>

              {/* Show price summary */}
              {selectedPlanMeta && (
                <div className={styles.selectedPlanInfo}>
                  {selectedPlanMeta.name} -{' '}
                  {!paymentRequired ? (
                    <>
                      <span className={styles.originalPrice}>${currentPrice}</span>{' '}
                      <strong>FREE</strong>
                    </>
                  ) : effectivePrice() !== currentPrice ? (
                    <>
                      <span className={styles.originalPrice}>${currentPrice}</span> $
                      {effectivePrice()}
                    </>
                  ) : (
                    <>${currentPrice}</>
                  )}
                  {selectedPlanType === 'subscription' ? '/year' : ''}
                </div>
              )}

              <div className={styles.wizardNav}>
                <button type="button" className={styles.wizardBackButton} onClick={goBack}>
                  Back
                </button>
                <button type="button" className={styles.wizardNextButton} onClick={goNext}>
                  {!paymentRequired ? 'Claim Access' : 'Continue to Payment'}
                </button>
              </div>
            </div>
          )}

          {/* ========== STEP 4: PAYMENT ========== */}
          {currentStep === 'payment' && (
            <div className={styles.wizardPanel}>
              <h2 className={styles.checkoutTitle}>
                {paymentRequired ? 'Complete Your Purchase' : 'Claim Your Free Access'}
              </h2>

              <div className={styles.selectedPlanInfo}>
                {selectedPlanMeta?.name || selectedPlan} -{' '}
                {!paymentRequired ? (
                  <>
                    <span className={styles.originalPrice}>${currentPrice}</span>{' '}
                    <strong>FREE</strong>
                  </>
                ) : effectivePrice() !== currentPrice ? (
                  <>
                    <span className={styles.originalPrice}>${currentPrice}</span> $
                    {effectivePrice()}
                  </>
                ) : (
                  <>${currentPrice}</>
                )}
                {selectedPlanType === 'subscription' ? '/year' : ''}
              </div>

              {customerEmail && (
                <div className={styles.wizardSummaryRow}>Email: {customerEmail}</div>
              )}

              {enteredCode && codeResult?.valid && (
                <div className={styles.wizardSummaryRow}>
                  Code: {enteredCode} ({codeResult.type === 'affiliate' ? 'Affiliate' : 'Coupon'})
                </div>
              )}

              {!paymentRequired ? (
                <div className={styles.freeClaimSection}>
                  {redeemError && <div className={styles.error}>{redeemError}</div>}
                  <button
                    type="button"
                    className={styles.freeAccessButton}
                    onClick={handleRedeemFreeCoupon}
                    disabled={redeemingCoupon}
                  >
                    {redeemingCoupon ? 'Claiming...' : 'Claim Free Access'}
                  </button>
                </div>
              ) : (
                <>
                  <p className={styles.termsNotice}>
                    By completing your purchase, you agree to our{' '}
                    <Link to="/terms-of-service" className={styles.termsLink}>
                      Terms of Service
                    </Link>
                    . All sales are final — no refunds.
                  </p>
                  <div className={styles.paymentInfo}>
                    <img src="/paypal-logo.svg" alt="PayPal" className={styles.paypalLogo} />
                    <p>Checkout available exclusively through PayPal</p>
                  </div>
                  <div className={styles.paypalContainer}>
                    {paypalError ? (
                      <div className={styles.error}>{paypalError}</div>
                    ) : !sdkReady ? (
                      <div className={styles.loading}>Loading payment options...</div>
                    ) : (
                      <div className={styles.buttonContainer}>
                        <div ref={paypalRef}></div>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className={styles.wizardNav}>
                <button type="button" className={styles.wizardBackButton} onClick={goBack}>
                  Back
                </button>
                <div />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
